import React, { useMemo, useState, useEffect } from 'react';
import { DollarSign, TrendingDown, TrendingUp } from 'lucide-react';
import { MasterTransaction } from '../types';
import { parseTransactionDate, matchesSource } from '../lib/utils';
import { isSubmitted, isAllocated, isSubmittedCash, isAllocatedCash, isSubmittedJar, isAllocatedJar, isSubmittedPOS, isAllocatedPOS, isRejected, isDashboardSubmitted, isDashboardSubmittedCash, isDashboardSubmittedJar, isDashboardSubmittedPOS, isDispute, getRelatedTransactionIds, isPendingFinalization, getPendingDisputeTotal } from '../lib/transactionUtils';
import { useFirebase } from '../contexts/FirebaseContext';
import { getPendingDisputes } from '../services/disputeApprovalService';
import { Dispute } from '../services/disputeService';
import { getStaff, Staff } from '../services/staffService';
import { formatCurrency } from '../lib/formatters';

interface Props {
  transactions: MasterTransaction[];
  allTransactions: MasterTransaction[];
  startDate?: string;
  endDate?: string;
}

export const AuditReconciliationTab: React.FC<Props> = ({ transactions, allTransactions, startDate, endDate }) => {
  const { tenantId } = useFirebase();
  const [pendingDisputes, setPendingDisputes] = useState<(Dispute & { id: string })[]>([]);
  const [staffList, setStaffList] = useState<Staff[]>([]);

  useEffect(() => {
    if (!tenantId) return;
    const fetchDisputes = async () => {
      const disputes = await getPendingDisputes(tenantId);
      setPendingDisputes(disputes || []);
    };
    const fetchStaffData = async () => {
      try {
        const list = await getStaff(tenantId);
        setStaffList(list || []);
      } catch (e) {
        console.error('Error fetching staff in reconciliation tab:', e);
      }
    };
    fetchDisputes();
    fetchStaffData();
  }, [tenantId]);

  const resolveStaff = (tx: MasterTransaction) => {
    const matched = staffList.find(s => 
      (tx.staffId && s.id === tx.staffId) ||
      (s.email && tx.email && s.email.toLowerCase().trim() === tx.email.toLowerCase().trim()) ||
      (s.name && tx.staffName && s.name.toLowerCase().trim() === tx.staffName.toLowerCase().trim()) ||
      (tx.posId && s.posId && s.posId.trim() === tx.posId.trim())
    );
    return {
      displayName: tx.staffName || matched?.name || tx.email || 'Unknown Staff',
      posId: tx.posId || matched?.posId || null,
      payrollId: matched?.payrollId || null,
      staffId: tx.staffId || matched?.id || null,
    };
  };

  const filteredTransactions = useMemo(() => {
    const baseFiltered = transactions.filter(t => {
      const transactionDate = parseTransactionDate(t.tipDate || t.timestamp);
      return (!startDate || transactionDate >= startDate) && (!endDate || transactionDate <= endDate);
    });
    
    // Expand to all related transactions from the full set
    const relatedIds = getRelatedTransactionIds(allTransactions, baseFiltered.map(t => t.id));
    return allTransactions.filter(t => relatedIds.has(t.id));
  }, [transactions, allTransactions, startDate, endDate]);

  const totalDisputeSubmitted = useMemo(() => getPendingDisputeTotal(pendingDisputes), [pendingDisputes]);
  
  const { totalCashSubmitted, totalJarSubmitted, totalPOSSubmitted, totalAllocated, totalPendingFinalization, totalSubmittedLedger } = useMemo(() => {
    let cash = 0, jar = 0, pos = 0, allocated = 0, pendingFinal = 0, submittedLedger = 0;

    for (const tx of filteredTransactions) {
      const amount = tx.amount || 0;
      const submittedAmount = tx.amount || tx.submittedAmount || 0;
      
      if (isDashboardSubmittedCash(tx, allTransactions)) cash += submittedAmount;
      if (isDashboardSubmittedJar(tx, allTransactions)) jar += submittedAmount;
      if (isDashboardSubmittedPOS(tx, allTransactions)) pos += submittedAmount;
      if (isDashboardSubmitted(tx, allTransactions)) submittedLedger += submittedAmount;
      
      if (isAllocated(tx, allTransactions) && tx.source !== 'Calculated') allocated += (tx.amount || tx.allocatedAmount || 0);
      if (isPendingFinalization(tx)) pendingFinal += amount;
    }

    return { 
      totalCashSubmitted: cash, 
      totalJarSubmitted: jar, 
      totalPOSSubmitted: pos, 
      totalAllocated: Math.round(allocated * 100) / 100, 
      totalPendingFinalization: pendingFinal,
      totalSubmittedLedger: submittedLedger
    };
  }, [filteredTransactions, allTransactions]);

  const totalSubmitted = useMemo(() => Math.round((totalSubmittedLedger + totalDisputeSubmitted) * 100) / 100, [totalSubmittedLedger, totalDisputeSubmitted]);

  // Combined Total
  const totalAllocatedWithPending = totalAllocated + totalPendingFinalization;
  
  // 3. Finalized/Verified Output
  const totalFinalized = useMemo(() => filteredTransactions
    .filter(tx => tx.auditVerified && isAllocated(tx, allTransactions))
    .reduce((acc, tx) => acc + (tx.amount || 0), 0), [filteredTransactions, allTransactions]);

  const variance = totalSubmitted - totalAllocated;

  // Identify Pending Finalization Transactions
  const pendingTransactions = useMemo(() => {
    const pending = filteredTransactions
    .filter(tx => isDashboardSubmitted(tx, allTransactions) && tx.allocationStatus !== 'Finalized' && !isRejected(tx));
    return pending;
  }, [filteredTransactions, allTransactions]);

  const totalPendingAmount = useMemo(() => pendingTransactions.reduce((acc, tx) => acc + (tx.amount || 0), 0), [pendingTransactions]);

  // Breakdown by Source (using pending + allocated for accuracy, preventing double-counting)
  const totalCashAllocated = useMemo(() => filteredTransactions.filter(tx => isAllocatedCash(tx, allTransactions) || (isDashboardSubmitted(tx, allTransactions) && tx.allocationStatus !== 'Finalized' && !isRejected(tx) && tx.source === 'cash')).reduce((acc, tx) => acc + (tx.amount || 0), 0), [filteredTransactions, allTransactions]);
  const totalJarAllocated = useMemo(() => filteredTransactions.filter(tx => isAllocatedJar(tx, allTransactions) || (isDashboardSubmitted(tx, allTransactions) && tx.allocationStatus !== 'Finalized' && !isRejected(tx) && tx.source === 'jar')).reduce((acc, tx) => acc + (tx.amount || 0), 0), [filteredTransactions, allTransactions]);
  const totalPOSAllocated = useMemo(() => filteredTransactions.filter(tx => isAllocatedPOS(tx, allTransactions) || (isDashboardSubmitted(tx, allTransactions) && tx.allocationStatus !== 'Finalized' && !isRejected(tx) && tx.source?.toLowerCase().includes('pos'))).reduce((acc, tx) => acc + (tx.amount || 0), 0), [filteredTransactions, allTransactions]);

  return (
    <div className="space-y-6 animate-in fade-in slide-in-from-top-2">
      <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
        <div className="p-6 bg-blue-50/50 rounded-2xl border border-blue-100/50">
          <h3 className="text-xs font-bold text-blue-600 uppercase tracking-wider mb-1">Cash Submitted</h3>
          <p className="text-2xl font-bold text-blue-900">{formatCurrency(totalCashSubmitted)}</p>
        </div>
        <div className="p-6 bg-blue-50/50 rounded-2xl border border-blue-100/50">
          <h3 className="text-xs font-bold text-blue-600 uppercase tracking-wider mb-1">Jar Submitted</h3>
          <p className="text-2xl font-bold text-blue-900">{formatCurrency(totalJarSubmitted)}</p>
        </div>
        <div className="p-6 bg-blue-50/50 rounded-2xl border border-blue-100/50">
          <h3 className="text-xs font-bold text-blue-600 uppercase tracking-wider mb-1">POS Submitted</h3>
          <p className="text-2xl font-bold text-blue-900">{formatCurrency(totalPOSSubmitted)}</p>
        </div>
        <div className="p-6 bg-blue-50/50 rounded-2xl border border-blue-100/50">
          <h3 className="text-xs font-bold text-blue-600 uppercase tracking-wider mb-1">Disputes Submitted</h3>
          <p className="text-2xl font-bold text-blue-900">{formatCurrency(totalDisputeSubmitted)}</p>
        </div>
        <div className="p-6 bg-blue-50/50 rounded-2xl border border-blue-100/50">
          <h3 className="text-xs font-bold text-blue-600 uppercase tracking-wider mb-1">Total Submitted</h3>
          <p className="text-2xl font-bold text-blue-900">{formatCurrency(totalSubmitted)}</p>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
        <div className="p-6 bg-emerald-50/50 rounded-2xl border border-emerald-100/50">
          <h3 className="text-xs font-bold text-emerald-600 uppercase tracking-wider mb-1">Allocated Tips</h3>
          <p className="text-2xl font-bold text-emerald-900">{formatCurrency(totalAllocated)}</p>
        </div>
        <div className={`p-6 rounded-2xl border flex items-center justify-between ${
          totalPendingFinalization !== 0 ? 'bg-amber-50/50 border-amber-100/50' : 'bg-green-50/50 border-green-100/50'
        }`}>
          <div>
            <h3 className={`text-xs font-bold uppercase tracking-wider mb-1 ${
              totalPendingFinalization !== 0 ? 'text-amber-600' : 'text-green-600'
            }`}>Pending Finalization</h3>
            <p className={`text-2xl font-bold ${
              totalPendingFinalization !== 0 ? 'text-amber-900' : 'text-green-900'
            }`}>{formatCurrency(totalPendingFinalization)}</p>
          </div>
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
            totalPendingFinalization !== 0 ? 'bg-amber-100 text-amber-600' : 'bg-green-100 text-green-600'
          }`}>
            {totalPendingFinalization > 0 ? <TrendingDown size={20} /> : totalPendingFinalization < 0 ? <TrendingUp size={20} /> : <DollarSign size={20} />}
          </div>
        </div>
        <div className="p-6 bg-purple-50/50 rounded-2xl border border-purple-100/50">
          <h3 className="text-xs font-bold text-purple-600 uppercase tracking-wider mb-1">Total Allocated (w/ Pending)</h3>
          <p className="text-2xl font-bold text-purple-900">{formatCurrency(totalAllocatedWithPending)}</p>
        </div>
      </div>

      {pendingTransactions.length > 0 && (
        <div className="p-6 bg-white rounded-2xl border border-amber-100 shadow-sm">
          <h3 className="text-sm font-bold text-amber-900 mb-4">Pending Finalization Details ({pendingTransactions.length})</h3>
          <div>
            {/* Mobile friendly Stacked Card layout for screen size < sm */}
            <div className="block sm:hidden space-y-3 pb-3">
              {pendingTransactions.map(tx => {
                const staff = resolveStaff(tx);
                return (
                <div key={tx.id} className="p-3 bg-amber-50/30 hover:bg-amber-50/60 transition-colors border border-amber-100/50 rounded-xl space-y-2 text-xs">
                  <div className="flex justify-between items-center text-[10px]">
                    <span className="font-mono text-gray-500 font-semibold">{tx.tipDate}</span>
                    <span className="px-2 py-0.5 rounded bg-amber-100/50 text-amber-800 font-black uppercase tracking-wider">{tx.source}</span>
                  </div>
                  <div className="flex justify-between items-start gap-2">
                    <div>
                      <span className="font-extrabold text-gray-900 block">{staff.displayName}</span>
                      <div className="flex flex-wrap items-center gap-1 mt-1">
                        {staff.posId && (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-blue-100 text-blue-800 text-[8px] font-mono font-bold uppercase">
                            POS ID: {staff.posId}
                          </span>
                        )}
                        {staff.payrollId && (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-purple-100 text-purple-800 text-[8px] font-mono font-bold uppercase">
                            Payroll ID: {staff.payrollId}
                          </span>
                        )}
                        {staff.staffId && (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-slate-200 text-slate-700 text-[8px] font-mono font-bold" title={`Staff ID: ${staff.staffId}`}>
                            Staff ID: {staff.staffId.slice(0, 8)}
                          </span>
                        )}
                      </div>
                    </div>
                    <span className="font-black text-gray-900 flex-shrink-0">{formatCurrency(tx.amount || 0)}</span>
                  </div>
                </div>
              );
              })}
              <div className="p-3 bg-amber-50/75 border border-amber-100 rounded-xl flex justify-between items-center text-xs">
                <span className="font-bold text-amber-900 uppercase tracking-wide">Total Pending</span>
                <span className="font-black text-amber-900">{formatCurrency(totalPendingAmount)}</span>
              </div>
            </div>

            {/* Classic Desktop view for screen size >= sm */}
            <div className="hidden sm:block overflow-x-auto">
              <table className="w-full text-xs text-left">
                <thead>
                  <tr className="border-b border-gray-100">
                    <th className="py-2 text-gray-400 font-bold uppercase">Date</th>
                    <th className="py-2 text-gray-400 font-bold uppercase">Staff</th>
                    <th className="py-2 text-gray-400 font-bold uppercase">Source</th>
                    <th className="py-2 text-gray-400 font-bold uppercase text-right">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {pendingTransactions.map(tx => {
                    const staff = resolveStaff(tx);
                    return (
                    <tr key={tx.id} className="border-b border-gray-50 last:border-0 hover:bg-amber-50/30">
                      <td className="py-3 font-mono">{tx.tipDate}</td>
                      <td className="py-3">
                        <div className="flex flex-col">
                          <span className="font-bold text-gray-900">{staff.displayName}</span>
                          <div className="flex flex-wrap items-center gap-1 mt-0.5">
                            {staff.posId && (
                              <span className="inline-flex items-center px-1.5 py-0.2 rounded bg-blue-100 text-blue-800 text-[8px] font-mono font-bold uppercase">
                                POS: {staff.posId}
                              </span>
                            )}
                            {staff.payrollId && (
                              <span className="inline-flex items-center px-1.5 py-0.2 rounded bg-purple-100 text-purple-800 text-[8px] font-mono font-bold uppercase">
                                PAY: {staff.payrollId}
                              </span>
                            )}
                            {staff.staffId && (
                              <span className="inline-flex items-center px-1.5 py-0.2 rounded bg-slate-200 text-slate-700 text-[8px] font-mono font-bold" title={`Staff ID: ${staff.staffId}`}>
                                ID: {staff.staffId.slice(0, 8)}
                              </span>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="py-3 uppercase font-black text-gray-500">{tx.source}</td>
                      <td className="py-3 font-bold text-right text-gray-900">{formatCurrency(tx.amount || 0)}</td>
                    </tr>
                  );
                  })}
                </tbody>
                <tfoot className="border-t-2 border-gray-100">
                   <tr>
                     <td className="py-3 font-bold text-gray-900" colSpan={3}>Total Pending</td>
                     <td className="py-3 font-black text-right text-amber-900">{formatCurrency(totalPendingAmount)}</td>
                   </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
